import { useEffect, useRef } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { appendChatMessage } from '@/store/slices/callSlice';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import {
  getInitialOrCaptureContactId,
  isChatContact,
  hasActiveCallLeg,
} from '@/components/amazon-connect/contactUtils';
import { connectParticipantToCallMessage } from '@/utils/chatMessages';
import { playChatMessageTone } from '@/utils/chatMessageTone';
import { notifyChatMessage } from '@/utils/desktopNotification';
import type { ConnectChatMessageEvent, ConnectContact } from '@/types';

function getContactScopeId(contact: ConnectContact): string {
  return getInitialOrCaptureContactId(contact) ?? contact.getContactId();
}

export function useChatConnectSubscriptions(activeContacts: ConnectContact[]) {
  const dispatch = useAppDispatch();
  const unsubscribersRef = useRef<Record<string, () => void>>({});

  useEffect(() => {
    const chatContacts = activeContacts.filter(
      contact => isChatContact(contact) && hasActiveCallLeg(contact)
    );
    const activeChatIds = new Set(chatContacts.map(getContactScopeId));

    for (const contactId of Object.keys(unsubscribersRef.current)) {
      if (!activeChatIds.has(contactId)) {
        unsubscribersRef.current[contactId]?.();
        delete unsubscribersRef.current[contactId];
      }
    }

    let cancelled = false;

    for (const contact of chatContacts) {
      const contactId = getContactScopeId(contact);
      if (unsubscribersRef.current[contactId]) continue;

      void connectAdapter
        .getChatMediaController(contact)
        .then(controller => {
          if (cancelled || unsubscribersRef.current[contactId]) return;
          const unsubscribe =
            typeof controller.onMessage === 'function'
              ? controller.onMessage((evt: ConnectChatMessageEvent) => {
                  const msg = connectParticipantToCallMessage(
                    evt.data ?? {},
                    true
                  );
                  if (!msg) return;
                  playChatMessageTone(msg);
                  notifyChatMessage(msg, contactId);
                  dispatch(appendChatMessage({ ...msg, contactId }));
                })
              : null;
          if (unsubscribe) {
            unsubscribersRef.current[contactId] = unsubscribe;
          }
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [activeContacts, dispatch]);

  useEffect(
    () => () => {
      for (const unsubscribe of Object.values(unsubscribersRef.current)) {
        unsubscribe();
      }
      unsubscribersRef.current = {};
    },
    []
  );
}
